import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Activity,
  Heart,
  Sparkles,
  Flame,
  Brain,
  Dumbbell,
  Stethoscope,
  Hourglass,
} from 'lucide-react';
import { healthApi } from '@/api/healthApi';
import MensHealthPreview from '@/components/MensHealthPreview';

/**
 * Men's Health Hub — top-level entry point that replaces the previous tab strip
 * with a structured, scannable list of male-specific health areas.
 *
 * Three card modes:
 *   - 'live'        fully built — drills into the existing form via `section`.
 *   - 'preview'     not yet fully built but worth a calm in-page preview that
 *                   reuses today's daily-tracker data. Tappable. Opens
 *                   <MensHealthPreview /> inline (not a modal, not a route).
 *   - 'coming-soon' not built. Card is non-clickable, dimmed (opacity-90),
 *                   no hover affordance. CTA reads "Coming Soon".
 *
 * Visual layout: icon · title · description · CTA · chevron, with a soft
 * coloured strip on the left for quick scanning.
 *
 * NO blocking modals or toasts anywhere in this flow — the spec is clear that
 * the experience must feel intentional even when a slice is unfinished.
 */
const SECTION_DEFINITIONS = [
  {
    key: 'daily',
    icon: Activity,
    title: 'Daily Health Tracking',
    description: 'Track your energy, stress, sleep, and daily habits.',
    cta: 'Track Now',
    accent: 'border-l-blue-400 bg-blue-50',
    iconWrap: 'bg-blue-100 text-blue-700',
    section: 'daily',
    mode: 'live',
  },
  {
    key: 'cardiovascular',
    icon: Heart,
    title: 'Cardiovascular Health',
    description: 'Monitor blood pressure, heart health, and circulation risks.',
    cta: 'Check Heart Health',
    accent: 'border-l-rose-400 bg-rose-50',
    iconWrap: 'bg-rose-100 text-rose-700',
    section: 'cardiovascular',
    mode: 'live',
  },
  {
    key: 'hormonal',
    icon: Sparkles,
    title: 'Hormonal Health',
    description: 'Understand testosterone, energy levels, mood, and hormonal balance.',
    cta: 'Check Hormonal Health',
    accent: 'border-l-amber-400 bg-amber-50',
    iconWrap: 'bg-amber-100 text-amber-700',
    route: '/hormonal-health?context=men',
    mode: 'live',
  },
  {
    key: 'sexual',
    icon: Flame,
    title: 'Sexual & Reproductive Health',
    description: 'Assess libido, performance, and reproductive health factors.',
    cta: 'Open preview',
    accent: 'border-l-orange-400 bg-orange-50',
    iconWrap: 'bg-orange-100 text-orange-700',
    mode: 'preview',
    pillLabel: 'Preview',
  },
  {
    key: 'mental',
    icon: Brain,
    title: 'Mental & Stress Health',
    description: 'Manage stress, mental resilience, and emotional wellbeing.',
    cta: 'Check Stress Levels',
    accent: 'border-l-purple-400 bg-purple-50',
    iconWrap: 'bg-purple-100 text-purple-700',
    route: '/mental-health?context=men',
    mode: 'live',
  },
  {
    key: 'fitness',
    icon: Dumbbell,
    title: 'Fitness & Strength',
    description: 'Track physical activity, strength, and body performance.',
    cta: 'Open preview',
    accent: 'border-l-emerald-400 bg-emerald-50',
    iconWrap: 'bg-emerald-100 text-emerald-700',
    mode: 'preview',
    pillLabel: 'Preview',
  },
  {
    key: 'screening',
    icon: Stethoscope,
    title: 'Preventive Screenings',
    description: 'Stay on top of health checks and early detection.',
    cta: 'Get Screening Plan',
    accent: 'border-l-teal-400 bg-teal-50',
    iconWrap: 'bg-teal-100 text-teal-700',
    section: 'screening',
    mode: 'live',
  },
  {
    key: 'age-risk',
    icon: Hourglass,
    title: 'Age-Based Risk',
    description: 'Understand risks like prostate health and long-term conditions.',
    cta: 'Open preview',
    accent: 'border-l-slate-400 bg-slate-50',
    iconWrap: 'bg-slate-200 text-slate-700',
    mode: 'preview',
    pillLabel: 'Preview',
  },
];

const microcopyForScore = (score) => {
  if (score == null) return null;
  if (score >= 70) return "You're on track — a few areas can improve.";
  if (score >= 50) return 'A few key areas need your attention today.';
  return "Let's improve your health step by step.";
};

const MensHealthHub = ({ onSelectSection }) => {
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    healthApi
      .getDailyTracker()
      .then((res) => {
        if (cancelled) return;
        const t = res?.data?.tracker;
        if (t) {
          setTracker(t);
          if (typeof t.health_score === 'number') setScore(t.health_score);
        }
      })
      .catch(() => {
        // Microcopy and previews are supportive — silently skip on failure.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const microcopy = useMemo(() => microcopyForScore(score), [score]);

  const handleCardClick = (def) => {
    if (def.mode === 'live') {
      if (def.route) {
        navigate(def.route);
        return;
      }
      onSelectSection(def.section);
      return;
    }
    if (def.mode === 'preview') {
      setPreviewKey(def.key);
      // Scroll to top so the preview lands cleanly in view.
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    // 'coming-soon' — non-clickable: handler is never wired (rendered as div).
  };

  if (previewKey) {
    return (
      <MensHealthPreview
        previewKey={previewKey}
        tracker={tracker}
        onBack={() => setPreviewKey(null)}
      />
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24"
      data-testid="mens-health-hub"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-5"
          data-testid="mens-health-hub-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* Section header */}
        <header className="mb-7">
          <h1
            className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight"
            data-testid="mens-health-hub-title"
          >
            Men's Health
          </h1>
          <p
            className="text-sm sm:text-base text-gray-600 mt-2 leading-relaxed"
            data-testid="mens-health-hub-subtitle"
          >
            Understand your body, improve performance, and stay ahead of risks.
          </p>
          {microcopy && (
            <p
              className="text-sm text-emerald-700 font-medium mt-3 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1"
              data-testid="mens-health-hub-microcopy"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {microcopy}
            </p>
          )}
        </header>

        {/* Cards */}
        <div className="space-y-3" data-testid="mens-health-hub-list">
          {SECTION_DEFINITIONS.map((def) => {
            const Icon = def.icon;

            // Per-mode visual tuning — coming-soon is rendered as a static
            // div (no button semantics) so screen readers don't announce it
            // as an interactive control. Preview / live use a real button.
            const isComingSoon = def.mode === 'coming-soon';
            const isPreview = def.mode === 'preview';
            const Wrapper = isComingSoon ? 'div' : 'button';
            const wrapperProps = isComingSoon
              ? { 'aria-disabled': true }
              : {
                  onClick: () => handleCardClick(def),
                  type: 'button',
                };

            const dimClass = isComingSoon
              ? 'opacity-90 cursor-default'
              : isPreview
              ? 'opacity-95 hover:opacity-100 hover:shadow-md active:scale-[0.99]'
              : 'hover:shadow-md active:scale-[0.99]';

            const ctaTextClass = isComingSoon
              ? 'text-gray-500'
              : 'text-gray-900 group-hover:gap-1.5';

            // Pill colour matches its meaning
            const pillClass = isComingSoon
              ? 'bg-gray-100 text-gray-500 border-gray-200'
              : isPreview
              ? 'bg-white text-amber-700 border-amber-200'
              : 'bg-white/70 text-gray-600 border-gray-200';

            return (
              <Wrapper
                key={def.key}
                {...wrapperProps}
                className={`group w-full text-left rounded-2xl border border-gray-100 ${def.accent} border-l-4 px-4 sm:px-5 py-4 shadow-sm transition-all ${dimClass}`}
                data-testid={`mens-health-card-${def.key}`}
                data-mode={def.mode}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${def.iconWrap}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-base font-bold text-gray-900 leading-tight">
                        {def.title}
                      </p>
                      {def.pillLabel && (
                        <span
                          className={`text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-md border ${pillClass}`}
                          data-testid={`mens-health-card-${def.key}-pill`}
                        >
                          {def.pillLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 leading-snug mt-1">
                      {def.description}
                    </p>
                    <p
                      className={`text-sm font-semibold mt-2.5 inline-flex items-center gap-1 transition-all ${ctaTextClass}`}
                    >
                      {def.cta}
                      {!isComingSoon && <ArrowRight className="h-3.5 w-3.5" />}
                    </p>
                  </div>
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MensHealthHub;
